//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import axios from './axios';
import { useConfig } from '../Config';
import Q from 'q';
import async from 'async';
import { User } from 'oidc-client-ts';
import { ResponseType } from 'axios';

type JSONPrimitive = string | number | boolean;
type JSONObject = { [key: string]: JSONValue };
type JSONArray = JSONValue[];
type JSONValue = JSONPrimitive | JSONObject | JSONArray | undefined;

interface QueueOptions {
  method: string;
  url: string;
  body: object;
  headers: { [key: string]: string };
  queryParameters: { [key: string]: string };
  form: FormData;
  deferred: Q.Deferred<any>;
  responseType: ResponseType;
}

function serializeQueryParams(params: { [key: string]: string }) {
  return Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
}

class LicenseDataService {
  private static instance: LicenseDataService;
  private readonly queue: async.QueueObject<QueueOptions>;
  constructor(getUserContext: () => User, getIsAuthenticated: () => boolean) {
    const { services } = useConfig();
    if (!LicenseDataService.instance) {
      LicenseDataService.instance = this;
    }

    this.queue = async.queue((options: QueueOptions, done) => {
      LicenseDataService.instance._request(
        options.method,
        options.url,
        options.body,
        options.headers,
        options.queryParameters,
        options.form,
        options.deferred,
        done,
        getUserContext,
        getIsAuthenticated,
        services?.license?.url,
        options.responseType,
      );
    }, 3);
  }

  /**
   * The main request method that all API methods in the LicenseDataService will eventually call.
   * @param method - http method
   * @param url - url to do request
   * @param body - body parameters / object
   * @param headers - header parameters
   * @param queryParameters - querystring parameters
   * @param form - form data object
   * @param deferred - promise object
   * @param completeJob - Call back to queue to communicate that job is done and the queue can pick up new job.
   * @param getUserContext - Function that returns user object to get access token from
   * @param getIsAuthenticated - Function that returns boolean value determining if user is currently authenticated
   * @param baseUrl - Request base url
   * @param responseType - Expected type of the response to the HTTP request, defaults to JSON
   */
  public _request(
    method: string,
    url: string,
    body: object,
    headers: { [key: string]: string },
    queryParameters: { [key: string]: string },
    form: FormData | object,
    deferred: Q.Deferred<any>,
    completeJob: () => void,
    getUserContext: () => User,
    getIsAuthenticated: () => boolean,
    baseUrl: string | undefined,
    responseType: ResponseType = 'json',
  ) {
    const user = getUserContext();
    const isAuthenticated = getIsAuthenticated();
    const query =
      queryParameters && Object.keys(queryParameters).length
        ? `?${serializeQueryParams(queryParameters)}`
        : '';
    const urlWithParams = `${url}${query}`;
    headers['Authorization'] = `Bearer ${user?.access_token}`;
    if (!isAuthenticated) {
      deferred.reject('User is not logged in.');
      completeJob();
      return;
    }
    const data = method === 'POST' && form ? form : body;
    axios({
      method,
      url: urlWithParams,
      baseURL: baseUrl,
      headers,
      responseType,
      data,
    })
      .then((response) => {
        deferred.resolve(response.data ?? response);
        completeJob();
      })
      .catch((error) => {
        deferred.reject(error.response);
        completeJob();
      });
  }

  public request<T>(
    method: string,
    url: string,
    body: object,
    headers: JSONValue,
    queryParameters: JSONValue,
    form: JSONValue,
    deferred: Q.Deferred<T>,
    responseType: ResponseType = 'json',
  ) {
    this.queue.push({
      method,
      url,
      body,
      headers: headers as { [key: string]: string },
      queryParameters: queryParameters as { [key: string]: string },
      form: form as unknown as FormData,
      deferred,
      responseType,
    });
  }

  public manageLicense(userId: string, tier: string) {
    const deferred = Q.defer();
    const requestBody = {
      id: userId,
      tier: tier,
    };

    this.request('PUT', '/user', requestBody, {}, {}, {}, deferred, 'json');

    return deferred.promise;
  }
  /**
   * Backwards-compatible alias used by existing components. Delegates to manageLicense (PUT /user).
   * This exists because callers still invoke licenseService.assignLicense after refactor.
   */
  public assignLicense(userId: string, tier: string) {
    return this.manageLicense(userId, tier);
  }
  public getLicenses() {
    const deferred = Q.defer();
    this.request('GET', '/licenses', {}, {}, {}, {}, deferred);
    return deferred.promise;
  }
}

export default LicenseDataService;
